import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, Sparkles } from 'lucide-react';
import QuickChallenge from './QuickChallenge';
import { heroStats } from '../mock';

const Hero = ({ onOpenOnboarding, onOpenAuth }) => {
  const [challengeKey, setChallengeKey] = useState(0);

  const startFree = () => {
    // Scroll to challenge on mobile, focus it on desktop
    setChallengeKey((k) => k + 1); // reset the challenge
    const el = document.getElementById('quick-challenge');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const navigate = useNavigate();
  const learnMore = () => navigate('/auth/signup');

  return (
    <section id="home" className="relative overflow-hidden">
      <BackgroundArt />
      <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 pt-8 sm:pt-12 lg:pt-24 pb-14 lg:pb-24 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div className="fade-up order-1">
          <div className="inline-flex items-center gap-2 text-[#F4C430] text-[10px] sm:text-xs tracking-[0.3em] sm:tracking-[0.35em] uppercase">
            <Sparkles size={14} /> Train &nbsp;·&nbsp; Improve &nbsp;·&nbsp; Evolve
          </div>
          <h1 className="mt-4 sm:mt-6 font-serif text-[38px] leading-[1.08] sm:text-5xl lg:text-[68px] lg:leading-[1.05] text-[#F5EDE0]">
            Discover
            <br /> how your
            <br /> <span className="text-[#F4C430] italic">brain thinks</span>
          </h1>
          <p className="mt-4 sm:mt-6 text-[#D9C9B3] text-base sm:text-lg max-w-md">
            Fun, daily and science-backed. Small daily challenges, big lifelong impact
          </p>

          <div className="mt-6 sm:mt-9 flex flex-wrap gap-3 sm:gap-4">
            <Button
              onClick={startFree}
              data-testid="hero-start-free-btn"
              className="rounded-full bg-[#F4C430] hover:bg-[#FFD34E] text-[#2A1810] font-semibold h-12 sm:h-14 px-5 sm:px-7 text-sm sm:text-base shadow-[0_12px_30px_-10px_rgba(244,196,48,0.55)]"
            >
              Start 5 Free Challenges
              <ArrowRight className="ml-1" size={18} />
            </Button>
            <Button
              onClick={learnMore}
              variant="outline"
              className="rounded-full h-12 sm:h-14 px-5 sm:px-7 text-sm sm:text-base bg-transparent border-[#7a5233] text-[#F5EDE0] hover:bg-[#3D2817] hover:text-[#F4C430] hover:border-[#F4C430]"
            >
              Learn More
            </Button>
          </div>

          <div className="mt-3 sm:mt-4 inline-flex items-center gap-2 text-xs sm:text-sm text-[#D9C9B3]" data-testid="hero-7day-badge">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#F4C430] shrink-0" />
            <span><span className="text-[#F4C430] font-semibold">Free Plan forever</span> <span className="text-[#B8A990]">· 7-day free trial on every paid plan</span></span>
          </div>

          <div className="mt-6 sm:mt-10 inline-flex items-center gap-3 sm:gap-4 rounded-full bg-[#3D2817]/60 border border-[#4a3221] pl-2 pr-4 sm:pr-5 py-1.5 sm:py-2">
            <div className="flex -space-x-2">
              {['A', 'B', 'C'].map((c, i) => (
                <div
                  key={c}
                  className="h-7 w-7 sm:h-8 sm:w-8 rounded-full grid place-items-center text-[11px] font-bold text-[#2A1810] border-2 border-[#2A1810]"
                  style={{ background: ['#F4C430', '#E8B923', '#FFD34E'][i] }}
                >
                  {c}
                </div>
              ))}
            </div>
            <div className="text-xs sm:text-sm">
              <span className="font-bold text-[#F5EDE0]">{heroStats.count}</span>{' '}
              <span className="text-[#B8A990]">{heroStats.label}</span>
            </div>
          </div>
        </div>

        <div className="relative order-2" id="quick-challenge">
          <BrainArt />
          <div className="relative z-10">
            <QuickChallenge
              key={challengeKey}
              onOpenAuth={onOpenAuth}
              onOpenOnboarding={onOpenOnboarding}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const BackgroundArt = () => (
  <>
    <div className="absolute -top-32 -left-20 w-[420px] sm:w-[520px] h-[420px] sm:h-[520px] rounded-full bg-[#5a3d24]/40 blur-[120px] pointer-events-none" />
    <div className="absolute -bottom-32 right-0 w-[360px] sm:w-[420px] h-[360px] sm:h-[420px] rounded-full bg-[#F4C430]/10 blur-[120px] pointer-events-none" />
    <div
      className="absolute inset-0 opacity-[0.06] pointer-events-none"
      style={{ backgroundImage: 'radial-gradient(#F4C430 1px, transparent 1px)', backgroundSize: '28px 28px' }}
    />
  </>
);

const BrainArt = () => (
  <div className="absolute inset-0 hidden lg:grid place-items-center pointer-events-none">
    <svg
      viewBox="0 0 500 500"
      className="w-[520px] h-[520px] brain-glow float-slow opacity-80"
      fill="none"
      stroke="#F4C430"
      strokeWidth="1"
    >
      {Array.from({ length: 60 }).map((_, i) => {
        const a = (i / 60) * Math.PI * 2;
        const r = 140 + (i % 5) * 12;
        const x = 250 + Math.cos(a) * r;
        const y = 250 + Math.sin(a) * r * 0.85;
        return <circle key={i} cx={x} cy={y} r="1.6" fill="#F4C430" />;
      })}
      {Array.from({ length: 40 }).map((_, i) => {
        const a1 = Math.random() * Math.PI * 2;
        const a2 = a1 + (Math.random() - 0.5);
        const r1 = 90 + Math.random() * 90;
        const r2 = 90 + Math.random() * 90;
        return (
          <line
            key={i}
            x1={250 + Math.cos(a1) * r1}
            y1={250 + Math.sin(a1) * r1}
            x2={250 + Math.cos(a2) * r2}
            y2={250 + Math.sin(a2) * r2}
            stroke="#F4C430"
            strokeOpacity="0.35"
          />
        );
      })}
    </svg>
  </div>
);

export default Hero;
